<!-- This component is used to display the payment step of the Everplans Enrollment Flow, if its in the cart -->
<template>
	<section class="flex flex-col gap-4 px-6 py-10">
		<div class="grid grid-cols-1 gap-4 *:w-full *:max-w-none">
			<FormKit
				type="dropdown"
				v-model="proposedInsured.everplansPayment.paymentType"
				@update:modelValue="setDefaultPaymentDetails"
				name="paymentType"
				:options="[
					{ label: t('document.eft'), value: PlanEverplansPaymentType.EFT },
					{
						label: t('document.creditCard'),
						value: PlanEverplansPaymentType.CreditCard,
					},
				]"
				:label="t('document.paymentType')"
				label-class="required"
				validation="required"
				inner-class="rounded-lg!"
			/>
			<FormKit
				type="email"
				id="everplansEmail"
				v-model="proposedInsured.everplansEmail"
				:label="t('person.email')"
				name="everplansEmail"
				label-class="required"
				validation="required|email|uniqueEverplansEmail"
				inner-class="rounded-lg!"
				:validation-rules="{ uniqueEverplansEmail }"
				validation-visibility="live"
				:validation-messages="{
					uniqueEverplansEmail: buildEmailErrorMessage,
				}"
			/>
		</div>

		<hr class="mt-5 mb-9 border-slate-200" />

		<FormKit
			#default="{ state: { valid } }"
			:key="`everplansPaymentDetails-${proposedInsured.id}`"
			type="form"
			:actions="false"
			form-class="w-full grow h-full shrink-0 relative"
			v-model="paymentDetailsModel"
		>
			<Transition
				name="fade"
				mode="out-in"
			>
				<EftFields
					v-if="everplansPayment.paymentType === PlanEverplansPaymentType.EFT"
					field-account-holder
					field-bank-name
					show-check
					v-model="everplansPayment.paymentDetails"
				/>
				<StripeForm
					v-else
					:validEverplansEmail="validEverplansEmail"
					v-model="creditCardPaymentDetailsModel"
				/>
			</Transition>
			<Teleport
				to="#everplansEnrollmentNext"
				v-if="
					sessionStore.getPlanHolder(sessionDocumentsStore.currentPlanHolderID)
						?.everplansStep === 'html' &&
					everplansPayment.paymentType === PlanEverplansPaymentType.EFT
				"
			>
				<button
					type="button"
					class="text-accent-700! h-12 w-52 justify-center rounded-md border-0 font-bold transition-all duration-300"
					:class="
						valid && validEverplansEmail
							? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
							: 'bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
					"
					:disabled="!valid || !validEverplansEmail"
					@click="handleAuthorizePayment(valid)"
				>
					{{ t('document.authorizePayment') }}
				</button>
			</Teleport>
		</FormKit>
	</section>
</template>

<script setup lang="ts">
import EftFields from '../EftFields.vue'
import type Plan from '@/classes/Plan'
import StripeForm from '@/components/documents/EverplansStep/StripeForm.vue'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { usePresentationLocale } from '@/i18n'
import { getBlankEft } from '@/mixins/eft'
import { useMerchandise } from '@/mixins/merchandise'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { CreditCard, EFT, EverplansPayment } from '@/types'
import type { FormKitNode } from '@formkit/core'
import { FormKit, useFormKitNodeById } from '@formkit/vue'
import { computed, onActivated, ref, watch } from 'vue'

const { t } = usePresentationLocale()
const { calculateCartTotals } = useMerchandise()
const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const planStore = usePlanStore()
const everplansEmailFieldKey = ref(0)

onActivated(() => {
	if (!proposedInsured.value.everplansEmail) {
		proposedInsured.value.everplansEmail = proposedInsured.value.email
	}

	// The enrollment step is kept alive while Finish & Review is displayed.
	// Recreate this field only when returning to the step, so FormKit rebuilds
	// its validation state from the latest plan data.
	everplansEmailFieldKey.value += 1
})

const proposedInsured = computed({
	get: () =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan,
	set: (value) => proposedInsured.value.update(value),
})

const everplansPayment = computed<EverplansPayment>(() => {
	return proposedInsured.value.everplansPayment as EverplansPayment
})

/** FormKit form bridge — keeps paymentDetails flat (EFT | CreditCard), not nested */
const paymentDetailsModel = computed({
	get: () => proposedInsured.value.everplansPayment.paymentDetails ?? {},
	set: (val) => {
		proposedInsured.value.everplansPayment.paymentDetails = val as
			| EFT
			| CreditCard
	},
})

const creditCardPaymentDetailsModel = computed<CreditCard | undefined>({
	get: () =>
		proposedInsured.value.everplansPayment.paymentDetails as
			| CreditCard
			| undefined,
	set: (val) => {
		proposedInsured.value.everplansPayment.paymentDetails = val as CreditCard
	},
})

// Whenever the plan holder changes, ensure price is updated payment is updated
// is there a everplansPayment for this plan holder, if so, restore it
// if there isn't then give it default payment details
// Everplans email must be defaulted to the plan holder's email if it is not already set
watch(
	proposedInsured,
	(value) => {
		proposedInsured.value.everplansPayment.amount =
			calculateCartTotals(value)?.everplansSubTotal

		if (!proposedInsured.value.everplansPayment.paymentDetails) {
			setDefaultPaymentDetails()
		}
	},
	{ immediate: true }
)

const setDefaultPaymentDetails = () => {
	const currentPaymentType = proposedInsured.value.everplansPayment.paymentType

	if (currentPaymentType === PlanEverplansPaymentType.EFT) {
		everplansPayment.value.paymentDetails = getBlankEft()
	} else if (currentPaymentType === PlanEverplansPaymentType.CreditCard) {
		creditCardPaymentDetailsModel.value = {
			token: '',
		}
	}
}

// The other plan holders in this session whose Everplans account claims the given email
const planNamesClaiming = (candidateEmail: string) =>
	planStore
		.findPlansClaimingEverplansEmail(proposedInsured.value, candidateEmail)
		.map((plan) => plan.firstName)

const nonUniqueEmailNames = computed(() =>
	planNamesClaiming(proposedInsured.value.everplansEmail ?? '')
)

/**
 * Reads node.value rather than the bound plan field because FormKit records which node
 * properties a rule touches and only re-runs it when one of them changes - a rule that
 * never reads the value is evaluated once and cached.
 */
const uniqueEverplansEmail = (node: FormKitNode): boolean =>
	planNamesClaiming(String(node.value ?? '')).length === 0

// Resolved once in setup - useFormKitNodeById is a composable, so calling it inside the
// computed would re-register a watcher on every re-evaluation
const everplansEmailField = useFormKitNodeById('everplansEmail')

const validEverplansEmail = computed(
	() =>
		nonUniqueEmailNames.value.length === 0 &&
		(everplansEmailField.value?.context?.state?.valid ?? false)
)

const buildEmailErrorMessage = () => {
	if (nonUniqueEmailNames.value.length === 0) {
		return ''
	}

	return t('document.everplansUniqueEmail', {
		names: nonUniqueEmailNames.value.join(', '),
		name: proposedInsured.value.firstName,
	})
}

const handleAuthorizePayment = (valid: boolean) => {
	if (!valid) {
		return
	}

	sessionDocumentsStore.setEverplansStepTo('document')
}
</script>

<style scoped>
@reference "tailwindcss";
:deep(.formkit-label.required::after) {
	@apply ml-1.5 text-red-500 content-['*'];
}
</style>
